import type { Phase } from "../../../../types/Phase";
import type {
  Prescription,
  PrescriptionSet,
  PrescriptionType,
  Reps,
} from "../../../../types/Prescription";
import type { BlockType } from "../../../../types/Block";
import type { WarmupProtocol } from "../../../../types/WarmupProtocol";
import type { ExerciseAlternative } from "../../../../types/ActionType";
import {
  ContentSources,
  buildParts,
  callGemini,
  parseJSON,
  sanitizeStringArray,
  JSON_RULES,
  LANGUAGE_RULE,
} from "../../onboarding/OnboardingAPI";

// ─── Prescription expansion ───────────────────────────────────────────────
// Gemini emits a row-style prescription that mirrors the PDF table:
//   { warmupSets, workingSets, reps, tempo?, earlySetRPE?, lastSetRPE?,
//     restMin, dropReps?, miniSetReps?, intraSetRestSec?, type }
// We expand that into the canonical Prescription shape:
//   { type, tempo?, restMin, sets: [...] }
// where `sets[]` is an ordered sequence of warmup + working entries. Warmup
// entries carry a `percent` (looked up from warmupProtocol.exerciseSpecific);
// working entries don't. Drop / triset / cluster modifiers attach to the
// relevant set entry.

const normalizeReps = (raw: any): Reps | undefined => {
  if (raw == null) return undefined;
  if (raw === "max") return "max";
  if (Array.isArray(raw) && raw.length === 2) {
    return [Number(raw[0]), Number(raw[1])];
  }
  if (typeof raw === "object") {
    if (typeof raw.min === "number" && typeof raw.max === "number") {
      return [raw.min, raw.max];
    }
    if (typeof raw.seconds === "number") {
      return { seconds: raw.seconds };
    }
  }
  return undefined;
};

const normalizeRange = (
  raw: any
): [number, number] | undefined => {
  if (!Array.isArray(raw) || raw.length !== 2) return undefined;
  const a = Number(raw[0]);
  const b = Number(raw[1]);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return undefined;
  return [a, b];
};

const normalizeTempo = (raw: any): Prescription["tempo"] | undefined => {
  if (!Array.isArray(raw) || raw.length !== 4) return undefined;
  const t = raw.map(Number);
  if (t.some((n) => !Number.isFinite(n))) return undefined;
  return t as Prescription["tempo"];
};

const normalizePrescriptionType = (raw: any): PrescriptionType => {
  if (
    raw === "normal" ||
    raw === "drop_set" ||
    raw === "cluster_set" ||
    raw === "triset"
  )
    return raw;
  return "normal";
};

const lookupWarmupPyramid = (
  warmupProtocol: WarmupProtocol | undefined,
  warmupSets: number
): Array<{ percentOfWorkingWeight: number; reps: [number, number] }> => {
  if (warmupSets <= 0) return [];
  const fromProtocol =
    warmupProtocol?.exerciseSpecific?.[String(warmupSets) as any];
  if (Array.isArray(fromProtocol) && fromProtocol.length === warmupSets) {
    return fromProtocol.map((entry: any) => ({
      percentOfWorkingWeight: Number(entry?.percentOfWorkingWeight) || 0.6,
      reps: normalizeRange(entry?.reps) ?? [6, 10],
    }));
  }
  // Fallback ramp (50% → 85%) when the protocol doesn't define this count.
  const ramp = [
    { percentOfWorkingWeight: 0.5, reps: [6, 10] as [number, number] },
    { percentOfWorkingWeight: 0.7, reps: [4, 6] as [number, number] },
    { percentOfWorkingWeight: 0.85, reps: [3, 4] as [number, number] },
  ];
  return ramp.slice(0, warmupSets);
};

function expandPrescription(
  raw: any,
  warmupProtocol: WarmupProtocol | undefined
): Prescription | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const type = normalizePrescriptionType(raw.type);
  const restMin = normalizeRange(raw.restMin) ?? [2, 3];
  const tempo = normalizeTempo(raw.tempo);
  const reps = normalizeReps(raw.reps);
  if (!reps) return undefined;

  const warmupCount = Math.max(0, Number(raw.warmupSets) || 0);
  const workingCount = Math.max(1, Number(raw.workingSets) || 1);
  const earlyRpe = normalizeRange(raw.earlySetRPE);
  const lastRpe = normalizeRange(raw.lastSetRPE);

  const pyramid = lookupWarmupPyramid(warmupProtocol, warmupCount);
  const warmupSets: PrescriptionSet[] = pyramid.map((entry) => ({
    percent: entry.percentOfWorkingWeight,
    reps: entry.reps as Reps,
  }));

  const workingSets: PrescriptionSet[] = [];
  for (let i = 0; i < workingCount; i++) {
    const isLast = i === workingCount - 1;
    const set: PrescriptionSet = { reps };
    const rpe = isLast ? lastRpe ?? earlyRpe : earlyRpe ?? lastRpe;
    if (rpe) set.rpe = rpe;
    if (isLast && type === "drop_set" && Array.isArray(raw.dropReps)) {
      const drops = raw.dropReps
        .map((n: any) => Number(n))
        .filter((n: number) => Number.isFinite(n));
      if (drops.length) set.dropReps = drops;
    }
    if (isLast && type === "triset") {
      if (Array.isArray(raw.miniSetReps)) {
        const minis = raw.miniSetReps
          .map((n: any) => Number(n))
          .filter((n: number) => Number.isFinite(n));
        if (minis.length) set.miniSetReps = minis;
      }
      if (Array.isArray(raw.variationLabels)) {
        const labels = raw.variationLabels
          .map((s: any) => (typeof s === "string" ? s.trim() : ""))
          .filter(Boolean);
        if (labels.length) set.variationLabels = labels;
      }
    }
    if (type === "cluster_set" && typeof raw.intraSetRestSec === "number") {
      set.intraSetRestSec = raw.intraSetRestSec;
    }
    workingSets.push(set);
  }

  return {
    type,
    tempo,
    restMin,
    sets: [...warmupSets, ...workingSets],
  };
}

export type WorkoutProgramDraft = {
  title: string;
  shortDescription: string;
  description: string;
  level?: string;
  tags?: string[];
  warmupProtocol?: WarmupProtocol;
  phases: WorkoutPhaseDraft[];
};

export type WorkoutPhaseDraft = Phase & {
  sessions: WorkoutSessionDraft[];
};

export type WorkoutSessionDraft = {
  title: string;
  description: string;
  position: number;
  restDayAfter: "mandatory" | "optional" | "none";
  blocks: WorkoutBlockDraft[];
  extractionStatus?: SessionExtractionStatus;
};

export type WorkoutBlockDraft = {
  type: BlockType;
  label?: string;
  position: number;
  subactions: WorkoutSubactionDraft[];
};

export type WorkoutSubactionDraft = {
  name: string;
  prescription: Prescription;
  alternatives?: ExerciseAlternative[];
  notes?: string;
};

export type WorkoutProgramProgressEvent =
  | { stage: "outline_started" }
  | { stage: "outline_done"; totalSessions: number }
  | { stage: "session_done"; completed: number; total: number }
  | { stage: "all_done"; totalSessions: number; totalExercises: number };

export type WorkoutProgramRequest = {
  sources: ContentSources;
  programName?: string;
  authorName?: string;
  communityName?: string;
  additionalGuidance?: string;
  onProgress?: (event: WorkoutProgramProgressEvent) => void;
};

// ─── Two-pass extraction ──────────────────────────────────────────────────
// Pass 1 returns only the program outline (metadata + per-phase session titles).
// Pass 2 makes one call per (phase, session) to extract that session's blocks.
// This avoids the single-pass failure mode where Gemini collapses 3+ sessions
// per phase into 1 because the model summarized instead of enumerated.

const OUTLINE_INSTRUCTION = `
You are reading a multi-phase workout program PDF and extracting its OUTLINE only.

DO NOT extract any exercises, sets, reps, tempo, RPE, or rest. Those are extracted in a separate pass. Your only job is metadata + the list of session titles per phase.

PDF LAYOUT
- Cover page with the program name
- Intro pages with phases overview
- Terminology pages: WUS, WS, Tempo, RIR/RPE
- Intensity technique definitions: Drop Set, Superset, Giant Set, Cluster Set, Triset
- Warmup protocol pages (general items + exercise-specific pyramid)
- Phase title pages (Phase One, Phase Two, Phase Three) — phases are typically 4 weeks each, 12 weeks total
- Workout tables — one per (week × session-type). Within a phase, weeks 2-4 repeat the same sessions as week 1.

WHAT TO EXTRACT FOR EACH PHASE
For each phase, open WEEK #1 of that phase and list EVERY distinct session-title heading (the bold heading directly above each workout table). A program usually has 2-4 sessions per phase. Common examples:
- "Lower Body", "Upper Body", "Full Body"
- "BACK & BICEPS & REAR DELT & TRAPS", "CHEST & DELTS & TRICEPS & ABS", "LEGS & ARMS"
- "Push", "Pull", "Legs"

Below each table is a "MANDATORY REST DAY" or "OPTIONAL REST DAY" marker — record that as restDayAfter.

WARMUP PROTOCOL
Extract from the warmup pages:
- general: items with name, optional duration, optional reps
- exerciseSpecific: object keyed by warmup-set count ("1", "2", "3") -> pyramid entries with percentOfWorkingWeight and reps

OUTPUT JSON (strict):
{
  "title": "string",
  "shortDescription": "string",
  "description": "string",
  "level": "Beginner" | "Intermediate" | "Advanced",
  "tags": ["string"],
  "warmupProtocol": {
    "general": [{ "name": "string", "duration": "string", "reps": "string" }],
    "exerciseSpecific": {
      "1": [{ "percentOfWorkingWeight": 0.60, "reps": [6, 10] }],
      "2": [{ "percentOfWorkingWeight": 0.50, "reps": [6, 10] }, { "percentOfWorkingWeight": 0.70, "reps": [4, 6] }],
      "3": [{ "percentOfWorkingWeight": 0.45, "reps": [6, 10] }, { "percentOfWorkingWeight": 0.65, "reps": [4, 6] }, { "percentOfWorkingWeight": 0.85, "reps": [3, 4] }]
    }
  },
  "phases": [
    {
      "phaseNumber": 1,
      "name": "Phase One",
      "description": "string",
      "weekRange": [1, 4],
      "coachNote": "string",
      "sessions": [
        { "title": "EXACT SESSION HEADING FROM PDF", "position": 1, "restDayAfter": "mandatory" | "optional" | "none" }
      ]
    }
  ]
}

CRITICAL:
- Preserve session titles VERBATIM from the PDF (capitalization, ampersands, spacing). The next pass uses these strings to find the matching table.
- Include EVERY session per phase. Most phases have 2-4 sessions. If you only see 1, look again — the PDF almost certainly has more.
- Each session has UNIQUE position starting at 1.
- Omit any field whose value is not present.
${LANGUAGE_RULE}
${JSON_RULES}
`.trim();

const SESSION_DETAIL_RULES = `
TABLE COLUMNS
EXERCISE | LAST SET INTENSITY TECHNIQUE | WARMUP SETS | WORKING SETS | REPS | TEMPO | EARLY SET RPE | LAST SET RPE | REST (min) | ALTERNATIVE EXERCISE #1 | ALTERNATIVE EXERCISE #2 | NOTES

BLOCKS
A session is a sequence of blocks.
- Standalone exercises (not under a SUPERSET label) -> block type "single" with one subaction.
- "SUPERSET #N" header with exactly 2 exercises -> block type "superset", label "SUPERSET #N".
- "SUPERSET #N" header with 3+ exercises -> block type "giant_set", label "SUPERSET #N".
- Block position increments by 1 starting at 0.

SUBACTIONS
Each table row is one subaction. Capture:
- name: exact exercise name from EXERCISE column (preserve casing and grip variations).
- prescription (see below).
- alternatives: from ALTERNATIVE EXERCISE #1 and #2. Skip entries that are "-" or blank.
- notes: from NOTES column.

PRESCRIPTION TYPE (from LAST SET INTENSITY TECHNIQUE column)
- "Normal set" -> "normal"
- "Drop set" / "Drop Set" -> "drop_set"
- "Cluster set" -> "cluster_set"
- "Triset" -> "triset"
- "Superset" -> "normal" (the grouping is captured at the BLOCK level, not here)

REPS NOTATION
- "8~12" or "8〜12" -> { "kind": "range", "min": 8, "max": 12 }
- "Max" -> "max"
- "60sec" / "30sec" -> { "kind": "duration_sec", "seconds": 60 }
- "21" (single number) -> { "kind": "range", "min": 21, "max": 21 }
- "8~12 / 8+8" with intensity "Drop set" -> reps: { "kind": "range", "min": 8, "max": 12 }, dropReps: [8]
- "10+10" with intensity "Drop set" -> reps: { "kind": "range", "min": 10, "max": 10 }, dropReps: [10]
- "10+10+10" with intensity "Triset" -> reps: { "kind": "range", "min": 10, "max": 10 }, miniSetReps: [10, 10, 10]

TEMPO: "2-0-1-1" -> [2, 0, 1, 1]. Always 4 numbers. Omit if blank or "-".
RPE: "7~8" -> [7, 8]. Omit if blank or "-".
REST: "2~3" -> [2, 3]. Always a min-max range. "1" -> [1, 1].

OUTPUT JSON (one session, no phase wrapper):
{
  "description": "string",
  "blocks": [
    {
      "type": "single" | "superset" | "giant_set",
      "label": "SUPERSET #1",
      "position": 0,
      "subactions": [
        {
          "name": "Leg extension",
          "prescription": {
            "type": "normal",
            "warmupSets": 2,
            "workingSets": 2,
            "reps": { "kind": "range", "min": 8, "max": 12 },
            "tempo": [2, 0, 1, 1],
            "earlySetRPE": [7, 8],
            "lastSetRPE": [8, 9],
            "restMin": [2, 3]
          },
          "alternatives": [{ "name": "Hack Squat" }, { "name": "Barbell squat" }],
          "notes": "Focus on full range of motion"
        }
      ]
    }
  ]
}

CRITICAL
- Preserve exact exercise names so the importer can dedup against the library.
- Each block has UNIQUE sequential position starting at 0.
- Omit any field whose value is not present.
- If you cannot find the target session in the PDF, return { "blocks": [] }.
${LANGUAGE_RULE}
${JSON_RULES}
`.trim();

type ProgramOutline = {
  title: string;
  shortDescription: string;
  description: string;
  level?: string;
  tags?: string[];
  warmupProtocol?: any;
  phases: Array<{
    phaseNumber: number;
    name: string;
    description: string;
    weekRange: [number, number];
    coachNote?: string;
    sessions: Array<{
      title: string;
      position: number;
      restDayAfter: "mandatory" | "optional" | "none";
    }>;
  }>;
};

type SessionDetailRaw = {
  description?: string;
  blocks?: any[];
};

type SessionExtractionStatus = "ok" | "ok_after_retry" | "failed";

type SessionExtractionOutcome = {
  detail: SessionDetailRaw;
  status: SessionExtractionStatus;
};

const SESSION_DETAIL_TOKENS = 32000;

const sessionDetailTargetedInstruction = (
  phaseName: string,
  sessionTitle: string,
  weekStart: number
) => `
You are extracting ONE specific workout session from a multi-phase workout program PDF.

TARGET
- Phase: "${phaseName}"
- Session: "${sessionTitle}"
- Week: #${weekStart} (the same workout repeats for every week in this phase — use WEEK #${weekStart} as the source)

Find the workout table in the PDF whose heading reads "${sessionTitle}" within "${phaseName}, WEEK #${weekStart}", and extract its blocks. Ignore all other phases, weeks, and sessions.

${SESSION_DETAIL_RULES}
`.trim();

const sessionDetailRelaxedInstruction = (
  phaseName: string,
  sessionTitle: string
) => `
You are extracting ONE specific workout session from a multi-phase workout program PDF.

This is a RETRY. A previous targeted attempt returned no exercises, so the previous prompt's week-pin and exact phase-name match were too strict. Be lenient on matching, but extract the FIRST week of the matching phase only (subsequent weeks repeat the same table).

TARGET
- Phase: "${phaseName}". The PDF may label this phase as "PHASE 1" / "PHASE 2" / "PHASE 3" rather than the spelled-out name. Match by phase NUMBER (One=1, Two=2, Three=3).
- Session: "${sessionTitle}". Match case-insensitively. Treat "&" and "and" as interchangeable. Ignore extra spaces.

Within the matching phase, find the FIRST week's table whose heading matches the session title (case-insensitive, ampersand-flexible). Note that the same session title may also appear in other phases — ignore those. If the matching phase has multiple sessions with similar names, pick the one whose heading is the closest match to "${sessionTitle}".

Extract that table's blocks using the rules below.

${SESSION_DETAIL_RULES}
`.trim();

async function extractProgramOutline(
  sources: ContentSources,
  contextLines: string[]
): Promise<ProgramOutline> {
  const fullInstruction = [
    OUTLINE_INSTRUCTION,
    "",
    "Context:",
    contextLines.length
      ? contextLines.map((line) => `- ${line}`).join("\n")
      : "- No additional context provided",
  ].join("\n");

  const parts = await buildParts(sources, fullInstruction);
  const text = await callGemini(parts, 8000, false);
  const raw = parseJSON<any>(text);
  return normalizeOutline(raw);
}

async function callSessionDetail(
  sources: ContentSources,
  instruction: string,
  logLabel: string
): Promise<SessionDetailRaw | null> {
  const parts = await buildParts(sources, instruction);
  const text = await callGemini(parts, SESSION_DETAIL_TOKENS, false);
  try {
    return parseJSON<SessionDetailRaw>(text);
  } catch (error) {
    console.warn(
      `[generateWorkoutProgram] ${logLabel} failed to parse:`,
      error
    );
    return null;
  }
}

async function extractSessionDetail(
  sources: ContentSources,
  phaseName: string,
  sessionTitle: string,
  weekStart: number
): Promise<SessionExtractionOutcome> {
  const label = `"${sessionTitle}" in "${phaseName}"`;

  const firstAttempt = await callSessionDetail(
    sources,
    sessionDetailTargetedInstruction(phaseName, sessionTitle, weekStart),
    `Targeted ${label}`
  );
  if (
    firstAttempt &&
    Array.isArray(firstAttempt.blocks) &&
    firstAttempt.blocks.length > 0
  ) {
    return { detail: firstAttempt, status: "ok" };
  }

  console.warn(
    `[generateWorkoutProgram] Targeted attempt returned no blocks for ${label}. Retrying with relaxed prompt.`
  );

  const retryAttempt = await callSessionDetail(
    sources,
    sessionDetailRelaxedInstruction(phaseName, sessionTitle),
    `Relaxed retry ${label}`
  );
  if (
    retryAttempt &&
    Array.isArray(retryAttempt.blocks) &&
    retryAttempt.blocks.length > 0
  ) {
    return { detail: retryAttempt, status: "ok_after_retry" };
  }

  console.warn(
    `[generateWorkoutProgram] Relaxed retry also returned no blocks for ${label}. Session will be marked as failed.`
  );
  return {
    detail: retryAttempt ?? firstAttempt ?? { blocks: [] },
    status: "failed",
  };
}

function normalizeOutline(raw: any): ProgramOutline {
  if (!raw || typeof raw !== "object") {
    throw new Error("AI did not return a valid program outline.");
  }
  const phases = Array.isArray(raw.phases) ? raw.phases : [];
  if (phases.length === 0) {
    throw new Error("Program outline is missing phases.");
  }
  return {
    title: typeof raw.title === "string" ? raw.title : "Untitled Program",
    shortDescription:
      typeof raw.shortDescription === "string" ? raw.shortDescription : "",
    description: typeof raw.description === "string" ? raw.description : "",
    level: typeof raw.level === "string" ? raw.level : undefined,
    tags: Array.isArray(raw.tags) ? sanitizeStringArray(raw.tags) : undefined,
    warmupProtocol:
      raw.warmupProtocol && typeof raw.warmupProtocol === "object"
        ? raw.warmupProtocol
        : undefined,
    phases: phases.map((p: any, i: number) => {
      const weekRange =
        Array.isArray(p?.weekRange) && p.weekRange.length === 2
          ? ([Number(p.weekRange[0]), Number(p.weekRange[1])] as [number, number])
          : ([i * 4 + 1, (i + 1) * 4] as [number, number]);
      const sessions = Array.isArray(p?.sessions) ? p.sessions : [];
      return {
        phaseNumber: typeof p?.phaseNumber === "number" ? p.phaseNumber : i + 1,
        name: typeof p?.name === "string" ? p.name : `Phase ${i + 1}`,
        description: typeof p?.description === "string" ? p.description : "",
        weekRange,
        coachNote: typeof p?.coachNote === "string" ? p.coachNote : undefined,
        sessions: sessions
          .filter((s: any) => typeof s?.title === "string" && s.title.trim())
          .map((s: any, si: number) => ({
            title: s.title.trim(),
            position: typeof s?.position === "number" ? s.position : si + 1,
            restDayAfter:
              s?.restDayAfter === "mandatory" ||
              s?.restDayAfter === "optional" ||
              s?.restDayAfter === "none"
                ? s.restDayAfter
                : "none",
          })),
      };
    }),
  };
}

export async function generateWorkoutProgram({
  sources,
  programName,
  authorName,
  communityName,
  additionalGuidance,
  onProgress,
}: WorkoutProgramRequest): Promise<WorkoutProgramDraft> {
  const contextLines: string[] = [];
  if (programName?.trim()) contextLines.push(`Program name: ${programName.trim()}`);
  if (authorName?.trim()) contextLines.push(`Author: ${authorName.trim()}`);
  if (communityName?.trim()) contextLines.push(`Community: ${communityName.trim()}`);
  if (additionalGuidance?.trim())
    contextLines.push(`Extra guidance: ${additionalGuidance.trim()}`);

  // Pass 1: outline
  onProgress?.({ stage: "outline_started" });
  const outline = await extractProgramOutline(sources, contextLines);

  // Pass 2: one Gemini call per (phase, session), in parallel.
  // For a 3-phase / 3-session-per-phase program that's 9 calls.
  const sessionJobs = outline.phases.flatMap((phase) =>
    phase.sessions.map((sessionOutline) => ({
      phase,
      sessionOutline,
    }))
  );

  onProgress?.({ stage: "outline_done", totalSessions: sessionJobs.length });

  let completed = 0;
  const sessionDetails = await Promise.all(
    sessionJobs.map(async (job) => {
      const detail = await extractSessionDetail(
        sources,
        job.phase.name,
        job.sessionOutline.title,
        job.phase.weekRange[0]
      );
      completed += 1;
      onProgress?.({
        stage: "session_done",
        completed,
        total: sessionJobs.length,
      });
      return detail;
    })
  );

  // Re-stitch: each phase keeps its outline order; merge detail by index.
  let jobCursor = 0;
  const phases: WorkoutPhaseDraft[] = outline.phases.map((phase) => {
    const sessions: WorkoutSessionDraft[] = phase.sessions.map((sessionOutline) => {
      const outcome = sessionDetails[jobCursor++];
      const detail = outcome?.detail ?? {};
      return normalizeWorkoutSessionDraft(
        {
          title: sessionOutline.title,
          position: sessionOutline.position,
          restDayAfter: sessionOutline.restDayAfter,
          description: detail.description,
          blocks: detail.blocks,
        },
        sessionOutline.position - 1,
        outline.warmupProtocol,
        outcome?.status ?? "failed"
      );
    });
    return {
      phaseNumber: phase.phaseNumber,
      name: phase.name,
      description: phase.description,
      weekRange: phase.weekRange,
      coachNote: phase.coachNote,
      sessions,
    };
  });

  const totalExercises = phases.reduce(
    (acc, phase) =>
      acc +
      phase.sessions.reduce(
        (sAcc, session) =>
          sAcc +
          session.blocks.reduce(
            (bAcc, block) => bAcc + block.subactions.length,
            0
          ),
        0
      ),
    0
  );
  onProgress?.({
    stage: "all_done",
    totalSessions: sessionJobs.length,
    totalExercises,
  });

  return {
    title: outline.title,
    shortDescription: outline.shortDescription,
    description: outline.description,
    level: outline.level,
    tags: outline.tags,
    warmupProtocol: outline.warmupProtocol,
    phases,
  };
}

function normalizeWorkoutSessionDraft(
  raw: any,
  index: number,
  warmupProtocol?: WarmupProtocol,
  extractionStatus?: SessionExtractionStatus
): WorkoutSessionDraft {
  const restDayAfter =
    raw?.restDayAfter === "mandatory" ||
    raw?.restDayAfter === "optional" ||
    raw?.restDayAfter === "none"
      ? raw.restDayAfter
      : "none";

  return {
    title: typeof raw?.title === "string" ? raw.title : `Session ${index + 1}`,
    description: typeof raw?.description === "string" ? raw.description : "",
    position: typeof raw?.position === "number" ? raw.position : index + 1,
    restDayAfter,
    blocks: Array.isArray(raw?.blocks)
      ? raw.blocks.map((b: any, i: number) =>
          normalizeWorkoutBlockDraft(b, i, warmupProtocol)
        )
      : [],
    extractionStatus,
  };
}

function normalizeWorkoutBlockDraft(
  raw: any,
  index: number,
  warmupProtocol?: WarmupProtocol
): WorkoutBlockDraft {
  const type: BlockType =
    raw?.type === "single" ||
    raw?.type === "superset" ||
    raw?.type === "giant_set"
      ? raw.type
      : "single";

  return {
    type,
    label: typeof raw?.label === "string" ? raw.label : undefined,
    position: typeof raw?.position === "number" ? raw.position : index,
    subactions: Array.isArray(raw?.subactions)
      ? raw.subactions.map((s: any) =>
          normalizeWorkoutSubactionDraft(s, warmupProtocol)
        )
      : [],
  };
}

function normalizeWorkoutSubactionDraft(
  raw: any,
  warmupProtocol?: WarmupProtocol
): WorkoutSubactionDraft {
  const alternatives: ExerciseAlternative[] | undefined = Array.isArray(
    raw?.alternatives
  )
    ? raw.alternatives
        .filter(
          (a: any) =>
            typeof a?.name === "string" &&
            a.name.trim() &&
            a.name.trim() !== "-"
        )
        .map((a: any) => ({ name: a.name.trim() }))
    : undefined;

  const expanded = expandPrescription(raw?.prescription, warmupProtocol);

  return {
    name: typeof raw?.name === "string" ? raw.name : "Exercise",
    prescription: expanded as Prescription,
    alternatives,
    notes:
      typeof raw?.notes === "string" && raw.notes.trim()
        ? raw.notes.trim()
        : undefined,
  };
}
